import { Collab, isWithinInfluencerCancelCutoff, isWithinInfluencerRescheduleCutoff, INFLUENCER_RESCHEDULE_CUTOFF_HOURS, MAX_INFLUENCER_RESCHEDULES, countInfluencerReschedules } from '@foodfluence/shared';
import { toDate } from 'utils/dateUtils';

/**
 * Maximum number of reschedule attempts allowed per collab.
 *
 * Re-exported from the shared guard rather than declared here: the inbox agent has to apply the
 * same limit before offering its Reschedule chip, and a second copy of the number would let the
 * chip and this screen disagree about who has attempts left.
 */
export const MAX_RESCHEDULE_ATTEMPTS = MAX_INFLUENCER_RESCHEDULES;


/**
 * Count the number of times a collab has been rescheduled
 * Uses the scheduleLogs to count 'influencer_reschedule' actions
 */
export const countRescheduleAttempts = (collab: Collab): number =>
  countInfluencerReschedules(collab?.scheduleLogs);

/**
 * Get the number of remaining reschedule attempts for a collab
 */
export const getRemainingReschedules = (collab: Collab): number => {
  const rescheduleCount = countRescheduleAttempts(collab);
  return Math.max(0, MAX_RESCHEDULE_ATTEMPTS - rescheduleCount);
};

/**
 * Check if a collab can be rescheduled (hasn't exceeded the limit)
 */
export const canRescheduleCollab = (collab: Collab): boolean => {
  return getRemainingReschedules(collab) > 0;
};

/**
 * Check if a collab is too close to its visit — or past it — for the creator to reschedule.
 *
 * Named for the rule rather than a number of hours, like the cancel gate beside it: the cutoff
 * lives in the shared guard, has already moved once (24h to 3h, when the inbox agent started
 * sending creators here instead of negotiating in the thread), and a helper called
 * isWithin24Hours that answers a 3-hour question is a trap.
 */
export const isWithinRescheduleCutoffOrAfter = (collab: Collab): boolean => {
  if (!collab?.scheduledCollabDate) return false;
  return isWithinInfluencerRescheduleCutoff(toDate(collab.scheduledCollabDate));
};

/**
 * Check if a collab is too close to its visit for the creator to cancel.
 *
 * Named for the rule rather than a number of hours: the cutoff lives in the shared guard and has
 * already moved once, and a helper called isWithin24Hours that answers a 3-hour question is a trap.
 * Delegating means the disabled Cancel link and the /collabs/cancel endpoint cannot drift apart.
 */
export const isWithinCancelCutoffOfCollab = (collab: Collab): boolean => {
  if (!collab?.scheduledCollabDate) return false;
  return isWithinInfluencerCancelCutoff(toDate(collab.scheduledCollabDate));
};

/**
 * Explain why a reschedule is blocked by the cutoff, so the user knows the specific reason
 * (visit already passed vs. too close to the visit).
 *
 * The number is interpolated from the shared constant rather than written out: this copy said
 * "24 hours" for a while after the rule itself moved, which tells creators a cutoff the code does
 * not enforce.
 */
export const getRescheduleCutoffBlockedMessage = (collab: Collab): string => {
  const scheduled = collab?.scheduledCollabDate ? toDate(collab.scheduledCollabDate) : null;
  const isPast = scheduled ? scheduled < new Date() : false;
  return isPast
    ? 'Your visit date has already passed, so rescheduling is closed. Please contact the brand if you still need to reschedule.'
    : `Your visit is less than ${INFLUENCER_RESCHEDULE_CUTOFF_HOURS} hours away, so rescheduling is closed. Please contact the brand if you need to reschedule.`;
};

/**
 * Get a message explaining why reschedule is not allowed
 */
export const getRescheduleBlockedMessage = (collab: Collab): string => {
  const rescheduleCount = countRescheduleAttempts(collab);
  const maxAttempts = MAX_RESCHEDULE_ATTEMPTS;
  return `You have already rescheduled this collab ${rescheduleCount} time${rescheduleCount > 1 ? 's' : ''}. Only ${maxAttempts} reschedule${maxAttempts > 1 ? 's are' : ' is'} allowed per collab to maintain trust with brands.`;
};

/**
 * A creator-initiated reschedule is hard-blocked when the collab is already scheduled and either
 * the max reschedule count is reached or the visit is within 24h / already past. A brand-requested
 * reschedule (clientRequestedReschedule) is the escape hatch — it is NEVER blocked, even inside the
 * 24h window. First-time scheduling (no date yet) is never a "reschedule" and is never blocked here.
 */
export const isRescheduleBlocked = (collab?: Collab): boolean => {
  if (!collab) return false;
  // Parsed, not merely present — a blank stored value is not a scheduled visit.
  const isDateAlreadyScheduled = !!toDate(collab.scheduledCollabDate);
  const isClientRescheduleRequest = !!collab.clientRequestedReschedule;
  return (
    isDateAlreadyScheduled &&
    !isClientRescheduleRequest &&
    (isWithinRescheduleCutoffOrAfter(collab) || !canRescheduleCollab(collab))
  );
};

/**
 * The reason a creator-initiated reschedule is blocked, or null when it isn't. Prefers the
 * 24h/past explanation over the max-reschedules one when both apply.
 */
export const getRescheduleBlockedReason = (collab?: Collab): string | null => {
  if (!isRescheduleBlocked(collab)) return null;
  return isWithinRescheduleCutoffOrAfter(collab as Collab)
    ? getRescheduleCutoffBlockedMessage(collab as Collab)
    : getRescheduleBlockedMessage(collab as Collab);
};
